<template>
  <div class="ai-assistant-page">
    <div class="layout">
      <aside class="chats-panel" :class="{ hidden: sidebarCollapsed }">
        <button class="new-chat-btn" :disabled="streaming" @click="newChat">
          Новый диалог
        </button>
        <div class="chats-list">
          <div v-if="openChatMenuId" class="chat-menu-overlay" @click="openChatMenuId = null"></div>
          <div
            v-for="chat in chats"
            :key="chat.id"
            class="chat-item"
            :class="{ active: chat.id === currentChatId }"
            @click="selectChat(chat.id)"
          >
            <span class="chat-title">{{ chatTitle(chat) }}</span>
            <button
              class="chat-menu-btn"
              title="Меню диалога"
              @click.stop="toggleChatMenu(chat.id)"
            >
              ⋮
            </button>
            <div v-if="openChatMenuId === chat.id" class="chat-menu">
              <button
                class="chat-menu-item danger"
                @click.stop="chatMenuDelete(chat)"
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      </aside>

      <div class="sidebar-divider"></div>

      <button
        class="sidebar-toggle"
        :class="{ collapsed: sidebarCollapsed }"
        :title="sidebarCollapsed ? 'Показать список диалогов' : 'Скрыть список диалогов'"
        @click="toggleSidebar"
      >
        <span class="sidebar-toggle-arrow">{{ sidebarCollapsed ? '⟩' : '⟨' }}</span>
      </button>

      <section class="chat-column">
        <div class="messages-wrap">
          <div ref="messagesBox"
            class="messages-box"
            :class="{ 'drag-over': dragOver }"
            @scroll="onScroll"
            @dragenter="onDragEnter"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @drop.prevent="onDrop"
          >
            <div ref="messagesInner" class="messages-inner" :class="{ narrow: uiSettings.narrowFeed }">
            <div v-if="loadingMessages" class="chat-loading">
              <span class="chat-spinner"></span>
            </div>
            <div v-else-if="!messages.length" class="empty-state">
              <p class="empty-title">Спросите что-нибудь о перевозках</p>
              <div class="empty-actions">
                <button
                  v-for="(hint, hIdx) in defaultQuestions"
                  :key="hIdx"
                  class="empty-question"
                  :disabled="streaming"
                  @click="sendText(hint)"
                >{{ hint }}</button>
              </div>
            </div>

            <div
              v-for="(msg, idx) in messages"
              :key="idx"
              class="message"
              :class="msg.role"
              :data-user-msg="msg.role === 'user' ? idx : null"
            >
              <div class="message-head">
                <span v-if="msg.role === 'user'">Вы</span>
                <span v-else>Ассистент</span>
                <button
                  v-if="msg.role === 'user' && msg.id && !streaming && editIdx === null"
                  class="msg-edit-btn"
                  title="Редактировать вопрос и задать заново"
                  @click="startEdit(msg, idx)"
                >✏️</button>
              </div>
              <div v-if="editIdx === idx" class="msg-edit">
                <textarea
                  ref="editInput"
                  v-model="editContent"
                  class="msg-edit-input"
                  rows="2"
                  @input="autoGrowEdit"
                  @keydown.enter.exact.prevent="submitEditUser(msg, idx)"
                  @keydown.esc="cancelEdit"
                ></textarea>
                <div class="msg-edit-bar">
                  <span class="msg-edit-hint">
                    Enter — отправить, Esc — отмена. Ветка от этого вопроса будет удалена.
                  </span>
                  <div class="msg-edit-actions">
                    <button class="msg-edit-cancel" @click="cancelEdit">Отмена</button>
                    <button
                      class="composer-send msg-edit-send"
                      :disabled="!editContent.trim()"
                      title="Отправить"
                      @click="submitEditUser(msg, idx)"
                    >
                      <font-awesome-icon :icon="['fa-solid', 'fa-paper-plane']" class="composer-send-icon" />
                    </button>
                  </div>
                </div>
              </div>
              <template v-else v-for="(item, bIdx) in renderItems(msg)" :key="bIdx">
                <AiMarkdown
                  v-if="item.block && item.block.type === 'text'"
                  :text="cleanMessageText(item.block.text)"
                  :final="true"
                  class="message-body-md"
                />
                <AiMarkdown
                  v-else-if="item.text !== undefined"
                  :text="cleanMessageText(item.text)"
                  :final="true"
                  class="message-body-md"
                />
                <AiBlock
                  v-else
                  :block="item.block"
                  :chips-interactive="!streaming"
                  class="message-blocks"
                  @chip-send="sendChip"
                />
              </template>
              <div v-if="msg.role === 'assistant' && (msg.tokens || msg.cost_rub || (msg.id && !streaming))" class="message-meta">
                <template v-if="msg.tokens">токенов: {{ msg.tokens }}</template>
                <template v-if="msg.tokens && msg.cost_rub"> · </template>
                <template v-if="msg.cost_rub">стоимость: {{ msg.cost_rub }} руб.</template>
                <button
                  v-if="msg.id && !streaming && editIdx === null"
                  class="regenerate-btn"
                  :disabled="streaming"
                  title="Переспросить: ветка от этого вопроса пересчитается"
                  @click="regenerateAnswer(msg, idx)"
                >↻ Ответить заново</button>
              </div>
            </div>

            <div v-if="assistantDraft" class="message assistant">
              <div class="message-head"><span>Ассистент</span></div>
              <AiTypingIndicator
                v-if="!assistantDraft.content && !assistantDraft.blocks.length && !assistantDraft.error"
                :phase="typingPhase"
              />
              <template v-for="(item, dIdx) in renderItems(assistantDraft)" :key="'d' + dIdx">
                <AiMarkdown
                  v-if="item.block && item.block.type === 'text'"
                  :text="cleanMessageText(item.block.text)"
                  :final="false"
                  class="message-body-md"
                />
                <AiMarkdown
                  v-else-if="item.text !== undefined"
                  :text="cleanMessageText(item.text)"
                  :final="false"
                  class="message-body-md"
                />
                <AiBlock
                  v-else
                  :block="item.block"
                  class="message-blocks"
                />
              </template>
              <div v-if="assistantDraft.error" class="message-error">{{ assistantDraft.error }}</div>
            </div>

            <div v-if="whatsNext.length && !streaming && !askForm" class="chips-row">
              <button
                v-for="(item, idx) in whatsNext"
                :key="idx"
                class="chips-row-item"
                :disabled="streaming"
                @click="sendChip(item)"
              >
                {{ item }}
              </button>
            </div>

            <div class="chat-disclaimer">
              Ассистент может ошибаться — проверяйте важные данные.
            </div>

            <div ref="scrollTail" class="scroll-tail"></div>
          </div>
        </div>

          <div v-if="railDots.length >= 1" class="questions-rail">
            <div
              v-for="dot in railDots"
              :key="dot.idx"
              class="rail-dot"
              @click="jumpTo(dot)"
            ><span class="dot-preview">{{ dot.text }}</span></div>
          </div>
        </div>

        <div ref="composerZone" class="composer-zone">
          <button v-if="unread" class="unread-badge" @click="jumpToBottom">
            ↓ Новые сообщения
          </button>

          <div v-if="streamError" class="stream-error">{{ streamError }}</div>
          <div v-if="uploadError" class="stream-error">{{ uploadError }}</div>
          <div v-if="uploading" class="composer-hint">Загружаю файл…</div>

          <div v-if="askForm" class="ask-form">
          <div class="ask-form-title">
            Ассистент уточняет
            <button class="ask-skip-btn" @click="skipAsk">Ответить текстом</button>
          </div>
          <div v-for="q in askForm" :key="q.id" class="ask-form-question">
            <div class="ask-form-text">
              {{ q.text }}<span v-if="q.optional" class="ask-form-optional"> (необязательно)</span>
            </div>
            <div class="ask-form-options">
              <label
                v-for="opt in (q.options || [])"
                :key="opt.value"
                class="ask-form-opt"
              >
                <input
                  v-if="q.mode === 'multi'"
                  type="checkbox"
                  :value="opt.value"
                  v-model="askMulti[q.id]"
                >
                <input
                  v-else
                  type="radio"
                  :name="'askq-' + q.id"
                  :value="opt.value"
                  v-model="askSingle[q.id]"
                >
                <span>{{ opt.label }}</span>
              </label>
            </div>
          </div>
          <textarea
            v-model="askFree"
            class="ask-free-input"
            rows="2"
            placeholder="Свой вариант или дополнение (необязательно)…"
          ></textarea>
          <div class="ask-form-actions">
            <button class="send-btn" @click="submitAsk">Ответить</button>
          </div>
        </div>

        <div v-else class="composer-wrap" :class="{ wide: !uiSettings.narrowFeed }">
          <div class="composer" @click="composerFocus">
            <textarea
              ref="draftInput"
              v-model="draft"
              class="composer-input"
              rows="1"
              placeholder="Введите вопрос…"
              :disabled="streaming"
              @input="autoGrowInput"
              @keydown.enter.exact.prevent="send"
            ></textarea>
              <div class="composer-bar">
                <div class="composer-bar-left">
                  <button
                    class="composer-settings-btn"
                    title="Настройки чата"
                    @click="openSettings('display')"
                  >
                    <font-awesome-icon :icon="['fa-solid', 'fa-gear']" />
                  </button>
                  <label
                    class="composer-settings-btn attach-btn"
                    :class="{ disabled: !canUpload }"
                    :title="canUpload
                      ? 'Прикрепить файл (csv, xlsx, parquet, до 50 МБ)'
                      : 'Дождитесь окончания ответа'"
                  >
                    <font-awesome-icon :icon="['fa-solid', 'fa-paperclip']" />
                    <input
                      ref="fileInput"
                      type="file"
                      class="file-input-hidden"
                      accept=".csv,.xlsx,.parquet"
                      multiple
                      :disabled="!canUpload"
                      @change="onFilePick"
                    >
                  </label>
                  <label
                    v-if="!messages.length && !loadingMessages"
                    class="composer-profile"
                    title="Пресет поведения ассистента — доступен только до первого сообщения"
                  >
                    <span class="composer-profile-label">Режим:</span>
                    <select
                      v-model="chatProfileId"
                      class="composer-profile-select"
                      :disabled="streaming"
                      @change="onProfileChange"
                    >
                      <option value="">Стандартный ассистент</option>
                      <option v-for="p in profiles" :key="p.id" :value="p.id">
                        {{ p.name }}
                      </option>
                    </select>
                  </label>
                  <span
                    v-else
                    class="composer-profile-static"
                    :title="'Режим диалога: ' + activeProfileName"
                  >{{ activeProfileName }}</span>
                </div>
              <button
                class="composer-send"
                :disabled="streaming || !draft.trim()"
                :title="streaming ? 'Ассистент отвечает…' : 'Отправить'"
                @click="send"
              >
                <span v-if="streaming" class="composer-send-spinner"></span>
                <font-awesome-icon v-else :icon="['fa-solid', 'fa-paper-plane']" class="composer-send-icon" />
              </button>
            </div>
          </div>
          <div v-if="streaming" class="composer-hint">Ассистент отвечает…</div>
        </div>
        </div>
      </section>
    </div>

    <div v-if="settingsOpen" class="memory-overlay" @click.self="settingsOpen = false">
      <div class="memory-modal settings-modal">
        <div class="memory-modal-head">
          <span class="memory-title">Настройки чата</span>
          <button class="memory-close" @click="settingsOpen = false">×</button>
        </div>
        <div class="memory-modal-body settings-body">
          <div class="settings-tabs">
            <button
              class="settings-tab"
              :class="{ active: settingsTab === 'display' }"
              @click="settingsTab = 'display'"
            >
              Отображение
            </button>
            <button
              v-if="memoryAvailable"
              class="settings-tab"
              :class="{ active: settingsTab === 'memory' }"
              @click="openSettings('memory')"
            >
              Память
            </button>
          </div>
          <div class="settings-content">
            <template v-if="settingsTab === 'display'">
              <label class="settings-check">
                <input
                  type="checkbox"
                  v-model="uiSettings.hideThinking"
                  @change="saveUiSettings"
                >
                <span>Скрывать мышление модели</span>
              </label>
              <label class="settings-check">
                <input
                  type="checkbox"
                  v-model="uiSettings.hideToolCalls"
                  @change="saveUiSettings"
                >
                <span>Скрывать вызовы инструментов</span>
              </label>
              <label class="settings-check">
                <input
                  type="checkbox"
                  v-model="uiSettings.narrowFeed"
                  @change="saveUiSettings"
                >
                <span>Узкая лента (по ширине окна ввода)</span>
              </label>
            </template>

            <template v-else>
            <div class="memory-sub">
              Заметки о вас видны только вам. Скажите боту «запомни …» —
              он сохранит сам, или добавьте вручную.
            </div>

          <div class="memory-add">
            <input
              v-model="memoryDraftTitle"
              class="memory-title-input"
              maxlength="300"
              placeholder="Заголовок (необязательно)"
            >
            <textarea
              v-model="memoryDraft"
              rows="3"
              maxlength="2000"
              class="memory-textarea"
              placeholder="Заметка о вас для ассистента…"
            ></textarea>
            <div class="memory-add-row">
              <button
                class="memory-add-btn"
                :disabled="!memoryDraft.trim() || memoryBusy"
                @click="addMemory"
              >
                Сохранить заметку
              </button>
            </div>
          </div>

          <div v-if="memoryError" class="memory-error">{{ memoryError }}</div>
          <div v-if="memoryBusy && !memories.length" class="memory-empty">Загрузка…</div>

          <div class="memory-list">
            <div v-if="!memories.length && !memoryBusy" class="memory-empty">
              Пока ничего нет
            </div>
            <div v-for="m in memories" :key="m.id" class="memory-item">
              <div class="memory-item-top">
                <span class="memory-date">{{ memoryDate(m) }}</span>
                <span class="memory-actions">
                  <button class="memory-act" @click="startEditMemory(m)">
                    изменить
                  </button>
                  <button class="memory-act del" @click="removeMemory(m)">удалить</button>
                </span>
              </div>
              <div v-if="m.title" class="memory-item-title">{{ m.title }}</div>
              <textarea
                v-if="memoryEditId === m.id"
                v-model="memoryEditText"
                rows="3"
                maxlength="2000"
                class="memory-textarea"
              ></textarea>
              <div v-else class="memory-text">{{ m.text }}</div>
              <div v-if="memoryEditId === m.id" class="memory-edit-row">
                <button class="memory-add-btn" @click="saveEditMemory(m)">Сохранить</button>
                <button class="memory-cancel-btn" @click="memoryEditId = null">Отмена</button>
              </div>
            </div>
          </div>
          </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  fetchAiStatus,
  fetchChats,
  createChat,
  deleteChat,
  fetchMessages,
  fetchActiveRun,
  trimAiMessage,
  streamChatAnswer,
  uploadChatFile,
  saveReport,
  fetchProfiles,
  setChatProfile,
  fetchMemories,
  createMemory,
  patchMemory,
  deleteMemory,
} from '@/backend/aiApi';
import AiBlock from './components/AiBlock.vue';
import AiMarkdown from './components/AiMarkdown.vue';
import AiTypingIndicator from './components/AiTypingIndicator.vue';
import { TOOL_LABELS } from './toolLabels';

const SCROLL_THRESHOLD_PX = 60;
// Модель пишет маркер по-разному: «### ЧТО-ЕЩЁ:», «ЧТО-ЕЩЁ:», «**ЧТО-ЕЩЁ:**».
// Дефис обязателен — чтобы не резать прозу «что ещё».
const WHATS_NEXT_RE = /[-#>\s*]*ЧТО[-–—]ЕЩЁ\s*(?:\*\*)?[ \t]*:/gi;
const WHATS_NEXT_PARTIALS = [
  '### ЧТО-ЕЩЁ:',
  'ЧТО-ЕЩЁ:',
  '**ЧТО-ЕЩЁ:**',
  '### ЧТО-ЕЩЁ',
  'ЧТО-ЕЩЁ',
];

// Скрываем служебный маркер ЧТО-ЕЩЁ из текста ответа: полный (после «|»)
// и частичный — пока маркер стримится по токенам («### ЧТО-», «### Ч»…).
function stripWhatsNext(raw) {
  const text = raw || '';
  WHATS_NEXT_RE.lastIndex = 0;
  let last = null;
  let match;
  while ((match = WHATS_NEXT_RE.exec(text)) !== null) last = match;
  if (last) return text.slice(0, last.index).replace(/\s+$/, '');
  const tail = text.slice(-20).toUpperCase();
  for (let len = Math.min(tail.length, 16); len >= 2; len -= 1) {
    const frag = tail.slice(tail.length - len);
    if (WHATS_NEXT_PARTIALS.some((v) => v.toUpperCase().startsWith(frag))) {
      return text.slice(0, text.length - len).replace(/\s+$/, '');
    }
  }
  return text;
}

// Модель дублирует file-артефакт markdown-ссылкой («Скачать файл») в тексте —
// плашка AiFileBlock уже даёт скачивание, ссылку из текста убираем.
const FILE_LINK_RE = /\[[^\]]*\]\([^)]*\/ai\/files\/[^)]*\)/g;

function stripFileLinks(raw) {
  return (raw || '')
    .replace(FILE_LINK_RE, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n');
}

function cleanMessageText(raw) {
  return stripWhatsNext(stripFileLinks(raw));
}

const SUGGESTION_POOL = [
  '📉 Какие направления проседают по маржинальности в этом месяце?',
  '🏆 Топ логистов по чистой комиссии за квартал',
  '⏸️ Сколько простоев подтверждено на складе X?',
  '📊 Покажи динамику рейсов по месяцам за последний год',
  '💰 Какая средняя маржинальность по рейсам за квартал?',
  '🚚 Топ перевозчиков по количеству выполненных рейсов',
  '⚠️ Рейсы с нарушением сроков доставки за последний месяц',
  '📦 Распределение грузов по типам кузова',
  '🗺️ Самые популярные маршруты за последние полгода',
  '🔁 Доля повторных заказов по клиентам',
  '🧾 Сравни выручку по месяцам этого и прошлого года',
  '🥇 Лучшие менеджеры по продажам за месяц',
  '🚫 Причины отменённых рейсов за квартал',
  '⛽ Средняя стоимость рейса по направлениям',
  '📈 Динамика средней ставки за километр',
  '🗓️ Загрузка по дням недели',
  '❓ Сколько рейсов без назначенного водителя прямо сейчас?',
  '🧮 Посчитай экономику сделки по маршруту Москва — Казань',
  '📋 Сводка по статусам всех активных рейсов',
  '🌐 Доля компании на рынке по ключевым направлениям',
];

function pickSuggestions(count = 6) {
  return [...SUGGESTION_POOL].sort(() => Math.random() - 0.5).slice(0, count);
}

export default {
  name: 'AiAssistantPage',
  components: { AiBlock, AiMarkdown, AiTypingIndicator },
  data() {
    return {
      status: null,
      chats: [],
      currentChatId: null,
      profiles: [],
      chatProfileId: '',
      messages: [],
      draft: '',
      streaming: false,
      streamError: '',
      assistantDraft: null,
      whatsNext: [],
      askForm: null,
      askSingle: {},
      askMulti: {},
      askFree: '',
      pendingAsk: null,
      unread: false,
      nearBottom: true,
      pollTimer: null,
      resuming: false,
      editIdx: null,
      editContent: '',
      railDots: [],
      defaultQuestions: [],
      sidebarCollapsed: false,
      loadingMessages: false,
      openChatMenuId: null,
      settingsOpen: false,
      settingsTab: 'display',
      uiSettings: { hideThinking: false, hideToolCalls: false, narrowFeed: true },
      memoryManager: false,
      memories: [],
      memoryBusy: false,
      memoryError: '',
      memoryDraft: '',
      memoryDraftTitle: '',
      memoryEditId: null,
      memoryEditText: '',
      uploading: false,
      uploadError: '',
      dragOver: false,
    };
  },
  computed: {
    canUpload() {
      return !this.streaming && !this.uploading;
    },
    memoryAvailable() {
      return !!(this.status && this.status.features && this.status.features.memory);
    },
    activeProfileName() {
      const profile = this.profiles.find((p) => p.id === this.chatProfileId);
      return (profile && profile.name) || 'Стандартный ассистент';
    },
    typingPhase() {
      const draft = this.assistantDraft;
      if (!draft) return 'думает…';
      if (draft.content) return 'печатает…';
      const steps = (draft.blocks || []).filter((b) => b.type === 'tool_call');
      for (let i = steps.length - 1; i >= 0; i -= 1) {
        if (steps[i].running) {
          const label = TOOL_LABELS[steps[i].tool] || steps[i].tool;
          return `инструмент: ${label}`;
        }
      }
      return this.resuming ? 'отвечает…' : 'думает…';
    },
  },
  mounted() {
    this.sidebarCollapsed = localStorage.getItem('ai-sidebar-collapsed') === '1';
    this.defaultQuestions = pickSuggestions();
    try {
      const saved = JSON.parse(localStorage.getItem('ai-ui-settings') || '{}');
      this.uiSettings = {
        hideThinking: !!saved.hideThinking,
        hideToolCalls: !!saved.hideToolCalls,
        narrowFeed: saved.narrowFeed !== false,
      };
    } catch (e) {
      this.uiSettings = { hideThinking: false, hideToolCalls: false, narrowFeed: true };
    }
    this.loadStatus();
    this.loadChats();
    this.loadProfiles();
    this.setupScrollObserver();
    this.setupComposerObserver();
  },
  beforeUnmount() {
    this.stopPoll();
    this.teardownScrollObserver();
  },
  methods: {
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem('ai-sidebar-collapsed', this.sidebarCollapsed ? '1' : '0');
    },
    toggleChatMenu(chatId) {
      this.openChatMenuId = this.openChatMenuId === chatId ? null : chatId;
    },
    chatMenuDelete(chat) {
      this.openChatMenuId = null;
      this.removeChat(chat);
    },
    chatTitle(chat) {
      return chat.title || 'Диалог без названия';
    },
    loadProfiles() {
      return fetchProfiles()
        .then((items) => {
          this.profiles = (items || []).filter((p) => p && p.is_active);
        })
        .catch(() => { this.profiles = []; });
    },
    // Смена пресета: применяется к текущему диалогу сразу, к новым — при создании.
    onProfileChange() {
      if (!this.currentChatId || this.streaming) return;
      const profileId = this.chatProfileId || null;
      setChatProfile(this.currentChatId, profileId)
        .then(() => {
          const chat = this.chats.find((c) => c.id === this.currentChatId);
          if (chat) chat.profile_id = profileId;
        })
        .catch(() => {});
    },
    loadStatus() {
      fetchAiStatus()
        .then((status) => {
          this.status = status;
          this.memoryManager = !!(status && status.can_manage_memory);
        })
        .catch(() => { this.status = null; });
    },
    loadChats() {
      return fetchChats()
        .then((chats) => {
          this.chats = chats || [];
          if (!this.currentChatId && this.chats.length) {
            this.selectChat(this.chats[0].id);
          }
        })
        .catch(() => { this.chats = []; });
    },
    newChat() {
      if (this.streaming) return;
      this.stopPoll();
      this.currentChatId = null;
      this.messages = [];
      this.loadingMessages = false;
      this.streamError = '';
      this.defaultQuestions = pickSuggestions();
      this.resetAsk();
      this.whatsNext = [];
      this.updateRail();
    },
    selectChat(chatId) {
      if (this.streaming || chatId === this.currentChatId) return;
      this.openChatMenuId = null;
      this.currentChatId = chatId;
      const chat = this.chats.find((c) => c.id === chatId);
      this.chatProfileId = (chat && chat.profile_id) || '';
      this.messages = [];
      this.loadingMessages = true;
      this.streamError = '';
      this.resetAsk();
      this.whatsNext = [];
      fetchMessages(chatId)
        .then((items) => {
          if (this.currentChatId !== chatId) return;
          this.messages = (items || [])
            .filter((m) => m.content || (m.blocks || []).length)
            .map((m) => this.normalizeMessage(m));
          this.restoreTailState();
          this.updateRail();
          this.scrollToEnd(true);
          this.checkActiveRun(chatId);
        })
        .catch(() => { this.messages = []; })
        .finally(() => {
          if (this.currentChatId === chatId) this.loadingMessages = false;
        });
    },
    removeChat(chat) {
      if (this.streaming) return;
      this.stopPoll();
      deleteChat(chat.id)
        .then(() => {
          this.chats = this.chats.filter((c) => c.id !== chat.id);
          if (this.currentChatId === chat.id) {
            this.currentChatId = null;
            this.messages = [];
          }
        })
        .catch(() => {});
    },
    // Резюм активного run: после refresh страницы стрим уже потерян, но run
    // продолжает считаться на бэкенде — поллим его и догружаем результат.
    checkActiveRun(chatId) {
      fetchActiveRun(chatId)
        .then((r) => {
          if (this.currentChatId !== chatId) return;
          if (r && r.active) {
            this.streaming = true;
            this.resuming = true;
            this.assistantDraft = { role: 'assistant', content: '', error: '', blocks: [] };
            this.whatsNext = [];
            this.pollTimer = setInterval(() => this.pollRun(chatId), 3000);
          }
        })
        .catch(() => {});
    },
    pollRun(chatId) {
      fetchActiveRun(chatId)
        .then((r) => {
          if (this.currentChatId !== chatId) {
            this.stopPoll();
            return;
          }
          if (!r || !r.active) {
            this.stopPoll();
            this.resuming = false;
            this.streaming = false;
            this.assistantDraft = null;
            fetchMessages(chatId)
              .then((items) => {
                if (this.currentChatId !== chatId) return;
                this.messages = (items || [])
                  .filter((m) => m.content || (m.blocks || []).length)
                  .map((m) => this.normalizeMessage(m));
                this.restoreTailState();
                this.scrollToEnd(true);
                this.updateRail();
              })
              .catch(() => {});
          }
        })
        .catch(() => {});
    },
    stopPoll() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
      this.resuming = false;
    },
    // Редактирование своего вопроса: ветка от него обрезается, вопрос задаётся заново.
    startEdit(msg, idx) {
      this.editIdx = idx;
      this.editContent = msg.content || '';
      this.$nextTick(() => {
        const el = this.$refs.editInput;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
        this.autoGrowEdit();
      });
    },
    autoGrowEdit() {
      const el = this.$refs.editInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    },
    cancelEdit() {
      this.editIdx = null;
      this.editContent = '';
    },
    submitEditUser(msg, idx) {
      const text = this.editContent.trim();
      if (!text || !msg.id || this.streaming) return;
      trimAiMessage(msg.id)
        .then(() => {
          this.messages = this.messages.slice(0, idx);
          this.cancelEdit();
          this.updateRail();
          this.sendText(text);
        })
        .catch(() => {});
    },
    // Повторный ответ без развилок: обрезаем ветку от исходного вопроса
    // (он удаляется вместе с ответом) и задаём его заново.
    regenerateAnswer(msg, idx) {
      if (this.streaming || !msg.id) return;
      let userIdx = -1;
      for (let i = idx - 1; i >= 0; i -= 1) {
        if (this.messages[i].role === 'user') {
          userIdx = i;
          break;
        }
      }
      if (userIdx < 0) return;
      const text = this.messages[userIdx].content;
      const userMsgId = this.messages[userIdx].id;
      const resend = () => {
        this.messages = this.messages.slice(0, userIdx);
        this.updateRail();
        this.sendText(text);
      };
      if (!userMsgId) {
        resend();
        return;
      }
      trimAiMessage(userMsgId)
        .then(resend)
        .catch(() => {});
    },
    // Восстановление хвостового состояния из истории: chips последнего ответа
    // и незакрытая форма уточнений (если на неё ещё не ответили).
    restoreTailState() {
      this.whatsNext = [];
      this.askForm = null;
      for (let i = this.messages.length - 1; i >= 0; i -= 1) {
        const msg = this.messages[i];
        if (msg.role !== 'assistant') break;
        const chips = (msg.blocks || []).find((b) => b.type === 'chips');
        if (chips && chips.items && chips.items.length) {
          this.whatsNext = chips.items;
        }
        const ask = (msg.blocks || []).find((b) => b.type === 'ask_user');
        if (ask && !ask.answers) {
          this.askForm = ask.questions || null;
          this.resetAskInputs(ask.questions);
        }
        break;
      }
    },
    resetAsk() {
      this.askForm = null;
      this.pendingAsk = null;
      this.resetAskInputs();
    },
    resetAskInputs(questions) {
      this.askSingle = {};
      this.askMulti = {};
      this.askFree = '';
      (questions || []).forEach((q) => {
        if (q.mode === 'multi') {
          this.askMulti[q.id] = [];
        } else {
          this.askSingle[q.id] = '';
        }
      });
    },
    skipAsk() {
      this.askForm = null;
    },
    submitAsk() {
      const lines = [];
      (this.askForm || []).forEach((q) => {
        const opts = {};
        (q.options || []).forEach((o) => { opts[o.value] = o.label; });
        const picked = q.mode === 'multi'
          ? (Array.isArray(this.askMulti[q.id]) ? this.askMulti[q.id] : []).map((v) => opts[v] || v)
          : (this.askSingle[q.id] ? [opts[this.askSingle[q.id]] || this.askSingle[q.id]] : []);
        if (picked.length) {
          lines.push(`${q.text}: ${picked.join(', ')}`);
        }
      });
      if (this.askFree.trim()) {
        lines.push(this.askFree.trim());
      }
      const answer = lines.length
        ? lines.join('\n')
        : 'Пропускаю уточнения, ответь по текущим данным';
      this.askForm = null;
      this.resetAskInputs();
      this.sendText(answer);
    },
    sendChip(item) {
      if (this.streaming) return;
      this.sendText(item);
    },
    onScroll() {
      const box = this.$refs.messagesBox;
      if (!box) return;
      const top = box.scrollTop;
      const distance = box.scrollHeight - top - box.clientHeight;
      // любое ЛЮБОЕ движение вверх отпускает автопин — иначе малые дельты
      // (трекпад) держат nearBottom=true и каждый токен дёргает обратно
      const goingUp = this._lastScrollTop !== undefined
        && top < this._lastScrollTop - 1
        && !this._pinning;
      this._lastScrollTop = top;
      if (goingUp) {
        this.nearBottom = false;
      } else if (distance < SCROLL_THRESHOLD_PX) {
        this.nearBottom = true;
      }
      if (this.nearBottom) this.unread = false;
    },
    // собственный программный пин к низу: помечаем _pinning, чтобы
    // наш же scroll event (в т.ч. при уменьшении высоты после репарса)
    // не интерпретировался как «юзер уехал вверх»
    pinToBottom() {
      const box = this.$refs.messagesBox;
      if (!box) return;
      this._pinning = true;
      box.scrollTop = box.scrollHeight;
      this._lastScrollTop = box.scrollTop;
      this.nearBottom = true;
      this.unread = false;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => { this._pinning = false; });
      });
    },
    scrollToEnd(force = false) {
      this.$nextTick(() => {
        if (!force && !this.nearBottom) return;
        this.pinToBottom();
      });
    },
    // Контент меняет высоту асинхронно (токены, таблицы/графики, репарс
    // markdown при finalize). Держим позицию КОМПЕНСАЦИЕЙ ДЕЛЬТЫ роста,
    // а не прыжком к абсолютному низу: даже если юзер только начал уезжать
    // вверх (scroll event ещё в полёте), рывок ограничен величиной роста.
    setupScrollObserver() {
      const inner = this.$refs.messagesInner;
      if (!inner || typeof ResizeObserver === 'undefined') return;
      this._innerHeight = inner.offsetHeight;
      this._scrollObserver = new ResizeObserver(() => {
        const box = this.$refs.messagesBox;
        if (!box) return;
        const height = inner.offsetHeight;
        const delta = height - this._innerHeight;
        this._innerHeight = height;
        if (delta < 0 && !this.nearBottom
            && this._lastScrollTop !== undefined) {
          // коллапс высоты (батч-репарс markdown на миг схлопывает DOM):
          // браузер уже клампил scrollTop вниз — возвращаем позицию
          // юзера относительно контента
          const target = Math.max(0, this._lastScrollTop + delta);
          this._pinning = true;
          box.scrollTop = target;
          this._lastScrollTop = target;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => { this._pinning = false; });
          });
          return;
        }
        if (delta <= 0 || !this.nearBottom) {
          // контент вырос, а юзер не у низа — вот теперь действительно
          // есть непрочитанное
          if (delta > 0 && !this.nearBottom) this.unread = true;
          return;
        }
        this._pinning = true;
        box.scrollTop += delta;
        this._lastScrollTop = box.scrollTop;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { this._pinning = false; });
        });
      });
      this._scrollObserver.observe(inner);
    },
    teardownScrollObserver() {
      if (this._scrollObserver) {
        this._scrollObserver.disconnect();
        this._scrollObserver = null;
      }
      if (this._composerObserver) {
        this._composerObserver.disconnect();
        this._composerObserver = null;
      }
    },
    // Композер наложен поверх ленты: хвост-зазор в ленте держит дисклеймер
    // ровно над зоной ввода при любом росте textarea / смене ask-формы.
    setupComposerObserver() {
      const zone = this.$refs.composerZone;
      if (!zone || typeof ResizeObserver === 'undefined') return;
      this._composerObserver = new ResizeObserver(() => {
        this.$nextTick(() => this.updateTailGap());
      });
      this._composerObserver.observe(zone);
      this.$nextTick(() => this.updateTailGap());
    },
    updateTailGap() {
      const zone = this.$refs.composerZone;
      const tail = this.$refs.scrollTail;
      if (!zone || !tail) return;
      const h = zone.offsetHeight + 4;
      if (tail._tailHeight !== h) {
        tail._tailHeight = h;
        tail.style.height = h + 'px';
      }
    },
    jumpToBottom() {
      this.nearBottom = true;
      this.$nextTick(() => this.pinToBottom());
    },
    jumpTo(dot) {
      const box = this.$refs.messagesBox;
      if (!box) return;
      const node = box.querySelector(`[data-user-msg="${dot.idx}"]`);
      if (!node) return;
      const top = Math.max(0, node.offsetTop - 80);
      // фиксируем намерение по цели перехода — иначе автопин вернёт к низу
      this.nearBottom = box.scrollHeight - top - box.clientHeight < SCROLL_THRESHOLD_PX;
      box.scrollTo({ top, behavior: 'smooth' });
    },
    updateRail() {
      // Реактивно из сообщений: DOM-тайминги AiMarkdown не влияют на точки.
      const dots = [];
      this.messages.forEach((msg, i) => {
        if (msg.role !== 'user' || !msg.content) return;
        const text = String(msg.content).trim().replace(/\s+/g, ' ');
        if (!text) return;
        dots.push({
          idx: String(i),
          text: text.length > 90 ? text.slice(0, 87) + '…' : text,
        });
      });
      this.railDots = dots;
    },
    renderItems(msg) {
      // Единая лента: если есть text-блок — рендерим по порядку blocks (интерлив),
      // иначе — контент первым, потом прочие блоки (старый формат сообщений).
      const hidden = this.uiSettings.hideThinking ? ['chips', 'thinking'] : ['chips'];
      if (this.uiSettings.hideToolCalls) hidden.push('tool_call');
      const all = (msg.blocks || []).filter((b) => b.type && !hidden.includes(b.type));
      // артефакты-выгрузки (file) — всегда в конец сообщения
      const blocks = all.filter((b) => b.type !== 'file');
      const files = all.filter((b) => b.type === 'file');
      const items = [];
      if (blocks.some((b) => b.type === 'text')) {
        blocks.forEach((b) => items.push({ block: b }));
      } else {
        if (msg.content) items.push({ text: msg.content });
        blocks.filter((b) => b.type !== 'text').forEach((b) => items.push({ block: b }));
      }
      files.forEach((b) => items.push({ block: b }));
      return items;
    },
    composerFocus(event) {
      if (event.target.closest('select, button, input, textarea')) return;
      const input = this.$refs.draftInput;
      if (input && !input.disabled) input.focus();
    },
    autoGrowInput() {
      const el = this.$refs.draftInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 400) + 'px';
    },
    canSaveReport(msg, idx) {
      if (msg.role !== 'assistant' || !msg.runId) return false;
      // Кнопка — у последнего сообщения с run_id (свежий прогон).
      const lastWithRun = [...this.messages]
        .reverse().find((m) => m.role === 'assistant' && m.runId);
      return lastWithRun === msg;
    },
    saveAsReport(msg) {
      if (!this.currentChatId || !msg.runId) return;
      const title = (msg.content || 'Отчёт').slice(0, 60);
      saveReport(this.currentChatId, { runId: msg.runId, title })
        .then(() => { this.$set ? this.$set(msg, 'reportSaved', true) : (msg.reportSaved = true); })
        .catch(() => { msg.reportSaveError = 'Не удалось сохранить отчёт'; });
    },
    handleEvent(event) {
      // SSE-события analytics-service:
      // {type: token|thinking|tool_start|tool_end|block|ask_user|error|done, ...}
      const draft = this.assistantDraft;
      if (!draft) return;
      if (event.type === 'token' && event.text) {
        draft.content += event.text;
        this.appendDraftText(event.text);
      } else if (event.type === 'thinking' && event.text) {
        const blocks = draft.blocks;
        const last = blocks.length && blocks[blocks.length - 1].type === 'thinking'
          ? blocks[blocks.length - 1]
          : null;
        if (last) last.text += event.text;
        else blocks.push({ type: 'thinking', text: event.text });
      } else if (event.type === 'tool_start') {
        draft.blocks.push({
          type: 'tool_call',
          id: event.id || null,
          tool: event.name,
          args: event.args || {},
          summary: null,
          running: true,
        });
      } else if (event.type === 'tool_end') {
        const step = this.findRunningStep(event);
        if (step) {
          step.summary = event.summary || {};
          step.running = false;
        }
      } else if (event.type === 'block' && event.block) {
        draft.blocks.push(event.block);
      } else if (event.type === 'ask_user' && event.questions) {
        this.pendingAsk = event.questions;
      } else if (event.type === 'error') {
        draft.error = event.detail;
      }
    },
    // Токены наращивают последний text-блок (или открывают новый) —
    // сегментация совпадает с backend-сборкой интерлива.
    appendDraftText(text) {
      const blocks = this.assistantDraft.blocks;
      const last = blocks.length && blocks[blocks.length - 1].type === 'text'
        ? blocks[blocks.length - 1]
        : null;
      if (last) last.text += text;
      else blocks.push({ type: 'text', text });
    },
    findRunningStep(event) {
      const steps = this.assistantDraft.blocks.filter((b) => b.type === 'tool_call');
      if (event.id) {
        const byId = steps.find((s) => s.id && s.id === event.id);
        if (byId) return byId;
      }
      return [...steps].reverse().find((s) => s.tool === event.name && s.running);
    },
    stripWhatsNext(raw) {
      return stripWhatsNext(raw);
    },
    cleanMessageText(raw) {
      return cleanMessageText(raw);
    },
    normalizeMessage(m) {
      return { ...m, runId: m.runId || m.run_id || null };
    },
    finalizeDraft(event) {
      const draft = this.assistantDraft;
      if (!draft) return;
      if (event && event.type === 'done' && event.message) {
        // авторитетная версия сообщения — как сохранено в БД
        this.messages.push(this.normalizeMessage({
          ...event.message,
          tokens: event.tokens,
          cost_rub: event.cost_rub,
        }));
        if (event.user_message_id) {
          for (let i = this.messages.length - 1; i >= 0; i -= 1) {
            const m = this.messages[i];
            if (m.role === 'user' && !m.id) {
              m.id = event.user_message_id;
              break;
            }
          }
        }
      } else {
        // стрим оборван — локальная сборка тем же алгоритмом интерлива
        draft.blocks = (draft.blocks || []).map((b) => (
          b.type === 'text' || b.type === 'thinking'
            ? { ...b, text: cleanMessageText(b.text) }
            : b
        ));
        draft.content = stripWhatsNext(draft.content);
        if (draft.content || draft.error || draft.blocks.length) {
          this.messages.push(draft);
        }
      }
      // Хвостовое состояние: chips и незакрытая форма уточнений.
      const lastMsg = this.messages[this.messages.length - 1];
      const chipsBlock = lastMsg && (lastMsg.blocks || []).find((b) => b.type === 'chips');
      this.whatsNext = event && event.type === 'done' && event.whats_next && event.whats_next.length
        ? event.whats_next
        : (chipsBlock && chipsBlock.items) || [];
      if (event && event.type === 'done' && event.ask_pending && this.pendingAsk) {
        this.askForm = this.pendingAsk;
        this.resetAskInputs(this.pendingAsk);
      }
      this.pendingAsk = null;
      this.assistantDraft = null;
      this.streaming = false;
      this.scrollToEnd();
      this.updateRail();
    },
    send() {
      this.sendText(this.draft.trim());
    },
    // --- Загрузка файлов юзера (csv/xlsx/parquet ≤50 МБ) ---
    onDragEnter(e) {
      if (!this.canUpload) return;
      e.preventDefault();
      this._dragDepth = (this._dragDepth || 0) + 1;
      this.dragOver = true;
    },
    onDragOver(e) {
      if (!this.canUpload) return;
      e.preventDefault();
    },
    onDragLeave() {
      this._dragDepth = Math.max(0, (this._dragDepth || 0) - 1);
      if (!this._dragDepth) this.dragOver = false;
    },
    onDrop(e) {
      this._dragDepth = 0;
      this.dragOver = false;
      if (!this.canUpload) return;
      const files = Array.from((e.dataTransfer && e.dataTransfer.files) || []);
      if (files.length) this.uploadFiles(files);
    },
    onFilePick(e) {
      const files = Array.from(e.target.files || []);
      e.target.value = '';
      if (files.length) this.uploadFiles(files);
    },
    uploadFiles(files) {
      const queue = [...files];
      this.uploading = true;
      this.uploadError = '';
      const next = () => {
        if (!queue.length || this.uploadError) {
          this.uploading = false;
          this.updateRail();
          return;
        }
        this.uploadOne(queue.shift()).then(next, () => { this.uploading = false; });
      };
      next();
    },
    checkUploadFile(file) {
      const name = (file.name || '').toLowerCase();
      if (!/\.(csv|xlsx|parquet)$/.test(name)) {
        return 'Поддерживаются только файлы csv, xlsx и parquet';
      }
      if (!file.size) return 'Файл пуст';
      if (file.size > 50 * 1024 * 1024) return 'Файл больше 50 МБ';
      return '';
    },
    uploadOne(file) {
      const bad = this.checkUploadFile(file);
      if (bad) {
        this.uploadError = bad;
        return Promise.reject(new Error(bad));
      }
      const ensureChat = this.currentChatId
        ? Promise.resolve(this.currentChatId)
        : createChat(`Файл: ${file.name}`.slice(0, 60), this.chatProfileId || null).then((chat) => {
            this.currentChatId = chat.id;
            return chat.id;
          });
      return ensureChat
        .then((chatId) => uploadChatFile(chatId, file))
        .then(({ message_id: messageId, block }) => {
          this.messages.push({
            id: messageId,
            role: 'user',
            content: `Загружен файл: ${block.filename}`,
            blocks: [block],
          });
          this.loadChats();
          this.scrollToEnd(true);
        })
        .catch((error) => {
          const resp = error && error.response && error.response.data;
          const detail = (resp && resp.detail) || (error && error.message) || String(error);
          this.uploadError = `Не удалось загрузить «${file.name}»: ${detail}`;
          throw error;
        });
    },
    sendText(question) {
      if (!question || this.streaming) return;
      this.draft = '';
      this.$nextTick(() => this.autoGrowInput());
      this.streamError = '';
      this.messages.push({ role: 'user', content: question });
      this.streaming = true;
      this.assistantDraft = {
        role: 'assistant', content: '', error: '', blocks: [],
      };
      this.scrollToEnd(true);
      this.updateRail();

      const startStream = (chatId) => {
        this.currentChatId = chatId;
        return streamChatAnswer({
          chatId,
          message: question,
          onEvent: (event) => this.handleEvent(event),
        });
      };

      const ensureChat = this.currentChatId
        ? Promise.resolve(this.currentChatId)
        : createChat(question.slice(0, 60), this.chatProfileId || null).then((chat) => {
            this.currentChatId = chat.id;
            return chat.id;
          });

      ensureChat
        .then((chatId) => startStream(chatId))
        .then((final) => {
          this.finalizeDraft(final);
          this.loadChats();
        })
        .catch((error) => {
          const message = error && error.message ? error.message : String(error);
          if (/уже выполняется/.test(message)) {
            // параллельный run в этом чате (другая вкладка) — подключаемся к нему
            this.assistantDraft = null;
            this.streaming = false;
            this.messages.pop();
            this.streamError = message;
            this.updateRail();
            this.checkActiveRun(this.currentChatId);
            return;
          }
          this.assistantDraft.error =
            error && error.name === 'AbortError'
              ? 'Прервано'
              : `Ошибка запроса: ${message}`;
          this.finalizeDraft(null);
        });
    },
    // --- Настройки чата / память ассистента ---
    openSettings(tab) {
      this.settingsOpen = true;
      this.settingsTab = tab;
      if (tab === 'memory') this.loadMemories();
    },
    saveUiSettings() {
      localStorage.setItem('ai-ui-settings', JSON.stringify(this.uiSettings));
    },
    loadMemories() {
      this.memoryBusy = true;
      this.memoryError = '';
      // только личная память агента о пользователе (без документов/публичных)
      return fetchMemories({ kind: 'personal' })
        .then((data) => {
          this.memoryManager = !!(data && data.manager);
          this.memories = (data && data.items) || [];
        })
        .catch((error) => {
          this.memoryError = `Не удалось загрузить: ${error && error.message ? error.message : error}`;
        })
        .finally(() => { this.memoryBusy = false; });
    },
    addMemory() {
      const text = this.memoryDraft.trim();
      if (!text) return;
      this.memoryBusy = true;
      this.memoryError = '';
      createMemory({
        text,
        kind: 'personal',
        title: this.memoryDraftTitle.trim() || undefined,
      })
        .then(() => {
          this.memoryDraft = '';
          this.memoryDraftTitle = '';
          return this.loadMemories();
        })
        .catch((error) => {
          this.memoryError = `Не удалось сохранить: ${error && error.message ? error.message : error}`;
          this.memoryBusy = false;
        });
    },
    startEditMemory(m) {
      this.memoryEditId = m.id;
      this.memoryEditText = m.text;
    },
    saveEditMemory(m) {
      const text = this.memoryEditText.trim();
      if (!text) return;
      patchMemory(m.id, { text })
        .then(() => { this.memoryEditId = null; return this.loadMemories(); })
        .catch((error) => { this.memoryError = error.message || 'Ошибка'; });
    },
    removeMemory(m) {
      deleteMemory(m.id)
        .then(() => this.loadMemories())
        .catch((error) => { this.memoryError = error.message || 'Ошибка'; });
    },
    memoryDate(m) {
      return (m.updated_at || m.created_at || '').slice(0, 10);
    },
  },
};
</script>

<style scoped>
.ai-assistant-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0;
}

.layout {
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 0;
}

.chats-panel {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 0;
  margin: 8px 0 8px 8px;
  transition: margin-left 0.25s ease, opacity 0.25s ease;
  overflow: hidden;
}

.sidebar-divider {
  width: 1px;
  background: #e5e7eb;
  align-self: stretch;
  flex-shrink: 0;
}

.chats-panel.hidden {
  margin-left: -248px;
  opacity: 0;
  pointer-events: none;
}

.sidebar-toggle {
  align-self: flex-start;
  width: 26px;
  height: 26px;
  border: 1px solid #e5e7eb;
  margin: 8px 0 0;
  border-radius: 6px;
  background: #f9fafb;
  color: #9ca3af;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: margin-left 0.25s ease;
}

.sidebar-toggle:hover {
  color: #2563eb;
  background: #eff6ff;
  border-color: #2563eb;
}

.sidebar-toggle.collapsed {
  margin-left: 8px;
}

.new-chat-btn {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 14px;
}

.new-chat-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.chats-list {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chat-item {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}

.chat-item:hover {
  background: #f3f4f6;
}

.chat-item.active {
  background: #e5e7eb;
  font-weight: 600;
}

.chat-title {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-menu-btn {
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  padding: 2px 4px;
  border-radius: 4px;
}

.chat-menu-btn:hover {
  color: #374151;
  background: #e5e7eb;
}

.chat-menu-overlay {
  position: fixed;
  inset: 0;
  z-index: 4;
}

.chat-menu {
  position: absolute;
  right: 6px;
  top: calc(100% + 2px);
  z-index: 5;
  min-width: 140px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 4px;
  display: flex;
  flex-direction: column;
}

.chat-menu-item {
  border: none;
  background: none;
  text-align: left;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  color: #374151;
  cursor: pointer;
}

.chat-menu-item:hover {
  background: #f3f4f6;
}

.chat-menu-item.danger {
  color: #dc2626;
}

.chat-menu-item.danger:hover {
  background: #fef2f2;
}

.chat-column {
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.chat-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
}

.chat-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #e5e7eb;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: ai-spin 0.8s linear infinite;
}

@keyframes ai-spin {
  to { transform: rotate(360deg); }
}

.messages-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
}

.messages-box {
  flex: 1;
  overflow-y: auto;
  /* браузерный scroll anchoring компенсирует scrollTop при репарсах
     markdown над вьюпортом — это и есть «отпрыгивание» */
  overflow-anchor: none;
  padding: 16px;
  padding-right: 22px;
}

.messages-inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 100%;
  box-sizing: border-box;
}

.messages-inner.narrow {
  width: 100%;
  max-width: 768px;
  margin: 0 auto;
}

.questions-rail {
  position: absolute;
  right: 24px;
  top: 16px;
  bottom: 16px;
  width: 10px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  user-select: none;
}

.rail-dot {
  position: relative;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2563eb;
  opacity: 0.45;
  cursor: pointer;
  flex: 0 0 auto;
  transition: transform 0.15s ease;
}

.rail-dot::after {
  content: '';
  position: absolute;
  inset: -8px;
}

.rail-dot:hover {
  opacity: 1;
  transform: scale(1.5);
}

.dot-preview {
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.4;
  color: #374151;
  white-space: nowrap;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
}

.rail-dot:hover .dot-preview {
  opacity: 1;
}

.unread-badge {
  align-self: center;
  margin-bottom: 10px;
  padding: 4px 14px;
  border: 1px solid #2563eb;
  border-radius: 14px;
  background: #fff;
  color: #2563eb;
  font-size: 12px;
  cursor: pointer;
  z-index: 3;
}

.unread-badge:hover {
  background: #eff6ff;
}

.empty-state {
  margin: auto;
  text-align: center;
  color: #6b7280;
  max-width: 768px;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
}

.empty-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 14px;
  color: #374151;
}

.empty-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  width: 100%;
}

.empty-question {
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-family: inherit;
  line-height: 1.45;
  padding: 16px;
  min-height: 84px;
  text-align: left;
  cursor: pointer;
  box-sizing: border-box;
  transition: border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.empty-question:hover {
  border-color: #2563eb;
  color: #2563eb;
}

.empty-question:disabled {
  opacity: 0.6;
  cursor: default;
}

.message {
  max-width: 80%;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
}

.message.user {
  align-self: flex-end;
  background: #e8f0fe;
}

.message.assistant {
  align-self: flex-start;
  background: transparent;
  border-radius: 0;
  padding: 2px 0;
  max-width: 100%;
}

.message-head {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 4px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.msg-edit-btn {
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  padding: 2px 4px;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.message:hover .msg-edit-btn {
  opacity: 0.55;
}

.msg-edit-btn:hover {
  opacity: 1;
}

.msg-edit {
  display: flex;
  flex-direction: column;
}

.msg-edit-input {
  width: 100%;
  box-sizing: border-box;
  border: none;
  background: rgba(255, 255, 255, 0.65);
  border-radius: 10px;
  resize: none;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.45;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px 12px;
  outline: none;
}

.msg-edit-input:focus {
  background: rgba(255, 255, 255, 0.9);
}

.msg-edit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 6px;
}

.msg-edit-hint {
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.4;
}

.msg-edit-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.msg-edit-cancel {
  border: none;
  background: none;
  color: #6b7280;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
}

.msg-edit-cancel:hover {
  background: #f3f4f6;
  color: #374151;
}

.msg-edit-send {
  width: 28px;
  height: 28px;
  font-size: 14px;
}

.message-body {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
}

.message-meta {
  margin-top: 6px;
  font-size: 11px;
  color: #9ca3af;
}

.regenerate-btn {
  border: none;
  background: none;
  color: #9ca3af;
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  margin-left: 10px;
}

.regenerate-btn:hover:not(:disabled) {
  color: #2563eb;
}

.regenerate-btn:disabled {
  opacity: 0.5;
  cursor: default;
}

.message-error {
  margin-top: 6px;
  color: #dc2626;
  font-size: 13px;
}

.message-blocks {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.report-actions {
  margin-top: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
}

.report-btn {
  padding: 4px 12px;
  border: 1px solid #2563eb;
  border-radius: 6px;
  background: #fff;
  color: #2563eb;
  cursor: pointer;
  font-size: 12px;
}

.report-btn:hover {
  background: #eff6ff;
}

.report-link {
  font-size: 12px;
  color: #2563eb;
  text-decoration: none;
  font-weight: 600;
}

.report-error {
  font-size: 12px;
  color: #dc2626;
}

.chips-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 2px 0 0;
  flex-shrink: 0;
}

.chips-row-item {
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  font-size: 13px;
  font-family: inherit;
  line-height: 1;
  padding: 7px 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
}

.chips-row-item:hover:not(:disabled) {
  background: #eff6ff;
  border-color: #2563eb;
  color: #2563eb;
}

.ask-form {
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  padding: 14px;
  margin: 0 16px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
  max-height: 65vh;
  overflow-y: auto;
}

.ask-form-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.ask-skip-btn {
  border: none;
  background: none;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
}

.ask-skip-btn:hover {
  color: #2563eb;
}

.ask-form-question {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ask-form-text {
  font-size: 13px;
  color: #111827;
}

.ask-form-optional {
  color: #9ca3af;
  font-size: 12px;
}

.ask-form-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
}

.ask-form-opt {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
}

.ask-free-input {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;
  resize: none;
}

.ask-form-actions {
  display: flex;
  justify-content: flex-end;
}

.composer-zone {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  pointer-events: none;
}

.composer-zone > * {
  pointer-events: auto;
}

.composer-wrap {
  width: 100%;
  max-width: calc(768px + 38px);
  margin: 0 auto;
  padding: 0 22px 14px 16px;
  box-sizing: border-box;
}

.composer-wrap.wide {
  max-width: none;
  padding: 0 16px 14px;
}

.composer {
  border: 1px solid #d1d5db;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  padding: 14px 14px 10px;
}

.composer:focus-within {
  border-color: #2563eb;
}

.composer-input {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  font-size: 14px;
  font-family: inherit;
  line-height: 1.45;
  max-height: 200px;
  overflow-y: auto;
  padding: 0;
  outline: none;
}

.composer-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 6px;
}

.composer-profile {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.composer-profile-label {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}

.composer-profile-select {
  border: none;
  background: transparent;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
  max-width: 260px;
  text-overflow: ellipsis;
  outline: none;
  padding: 2px 0;
}

.composer-profile-select:disabled {
  cursor: default;
  color: #9ca3af;
}

.composer-bar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.composer-profile-static {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 320px;
}

.composer-settings-btn {
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.composer-settings-btn:hover {
  color: #2563eb;
  background: #eff6ff;
}

.attach-btn.disabled {
  opacity: 0.45;
  cursor: default;
}

.attach-btn.disabled:hover {
  color: #9ca3af;
  background: transparent;
}

.file-input-hidden {
  display: none;
}

.messages-box.drag-over {
  box-shadow: inset 0 0 0 2px #2563eb;
  background: #f8fafc;
}

.settings-tabs {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 4px 12px 0;
  min-width: 170px;
  border-right: 1px solid #e5e7eb;
  align-self: stretch;
}

.settings-tab {
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  color: #6b7280;
  padding: 8px 16px;
  text-align: left;
  white-space: nowrap;
}

.settings-tab.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
  font-weight: 600;
}

.memory-modal-body.settings-body {
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
}

.settings-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
}

.settings-check {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  color: #374151;
  cursor: pointer;
  padding: 4px 0;
}

.settings-check input {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
}

.composer-send {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.composer-send:hover:not(:disabled) {
  background: #1d4ed8;
}

.composer-send:disabled {
  background: #d1d5db;
  cursor: default;
}

.composer-send-icon {
  display: block;
  font-size: 13px;
  line-height: 1;
  transform: translateX(-1px);
}

.composer-send-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ai-spin 0.8s linear infinite;
}

.composer-hint {
  margin-top: 6px;
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
}

.chat-disclaimer {
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
  margin-top: auto;
  margin-bottom: 4px;
  padding: 2px 16px 0;
}

.scroll-tail {
  flex-shrink: 0;
}

.send-btn {
  align-self: flex-end;
  padding: 8px 18px;
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
}

.send-btn:disabled {
  background: #93c5fd;
  cursor: default;
}

.stream-error {
  padding: 0 16px 8px;
  text-align: center;
  color: #dc2626;
  font-size: 13px;
}

/* --- Модалка памяти --- */

.memory-overlay {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.memory-modal {
  display: flex;
  flex-direction: column;
  width: 820px;
  max-width: 94vw;
  height: min(680px, 88vh);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.memory-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.settings-modal .memory-modal-head {
  border-bottom: none;
  padding-bottom: 0;
}

.memory-modal-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
}

.memory-title {
  font-size: 16px;
  font-weight: 600;
}

.memory-close {
  border: none;
  background: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}

.memory-close:hover {
  color: #dc2626;
}

.memory-sub {
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
}

.memory-add {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;
  background: #f9fafb;
  padding: 10px;
}

.memory-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
}

.memory-add-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.memory-add-btn {
  margin-left: auto;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  font-size: 13px;
}

.memory-add-btn:disabled {
  background: #93c5fd;
  cursor: default;
}

.memory-cancel-btn {
  padding: 6px 14px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}

.memory-error {
  color: #dc2626;
  font-size: 12px;
}

.memory-empty {
  color: #9ca3af;
  font-size: 13px;
  text-align: center;
  padding: 16px 0;
}

.memory-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.memory-item {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px;
}

.memory-item-top {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.memory-date {
  color: #9ca3af;
  font-size: 11px;
}

.memory-actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

.memory-act {
  border: none;
  background: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}

.memory-act:hover {
  color: #2563eb;
}

.memory-act.del:hover {
  color: #dc2626;
}

.memory-text {
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.memory-item-title {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 4px;
}

.memory-title-input {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
}

.memory-edit-row {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

@media (max-width: 768px) {
  .composer-wrap {
    padding: 0 10px 10px;
  }

  .empty-state {
    padding: 0 10px;
  }

  .messages-box {
    padding: 12px;
    padding-right: 18px;
  }
}
</style>
